<template>
	<BModal
		:model-value="visible"
		:title="title"
		:size="size"
		:button-size="buttonSize || undefined"
		:centered="centered"
		:ok-only="!confirm"
		:ok-title="okTitle"
		:cancel-title="cancelTitle"
		:ok-variant="okVariant"
		:cancel-variant="cancelVariant"
		:autofocus-button="autoFocusButton"
		:no-close-on-backdrop="noCloseOnBackdrop"
		:no-close-on-esc="noCloseOnEsc"
		:no-header-close="hideHeaderClose"
		:content-class="contentClass"
		:header-class="headerClass"
		:body-class="bodyClass"
		:footer-class="footerClass"
		:dialog-class="dialogClass"
		initial-animation
		@hide="onHide"
		@hidden="onHidden"
		@update:model-value="visible = $event">
		<template #header="{ close }">
			<h5 class="modal-title">
				{{ title }}
			</h5>
			<button v-if="!hideHeaderClose" type="button"
				class="close" aria-label="Close"
				@click="close">
				<span aria-hidden="true">&times;</span>
			</button>
		</template>

		<MessageBoxBody :message="message" />
	</BModal>
</template>

<script setup>
	import { cloneVNode, defineComponent, h, isVNode, onMounted, ref } from 'vue';
	import BModal from '@js/components/bvn-extras/BModal.vue';

	defineProps({
		message: { type: [String, Array, Object], default: '' },
		title: { type: String, default: 'Confirm' },
		okTitle: { type: String, default: 'OK' },
		cancelTitle: { type: String, default: 'Cancel' },
		okVariant: { type: String, default: 'primary' },
		cancelVariant: { type: String, default: 'secondary' },
		size: { type: String, default: 'sm' }, // one of: sm, md, lg, xl, empty
		buttonSize: { type: String, default: '' }, // one of: sm, lg, empty
		centered: { type: Boolean, default: true },
		confirm: { type: Boolean, default: false },
		noCloseOnBackdrop: { type: Boolean, default: false },
		noCloseOnEsc: { type: Boolean, default: false },
		hideHeaderClose: { type: Boolean, default: true },
		autoFocusButton: { type: String, default: 'ok' }, // one of: ok, cancel
		contentClass: { type: [String, Array, Object], default: '' },
		headerClass: { type: [String, Array, Object], default: '' },
		bodyClass: { type: [String, Array, Object], default: '' },
		footerClass: { type: [String, Array, Object], default: '' },
		dialogClass: { type: [String, Array, Object], default: '' },
	});

	const emit = defineEmits(['resolve']);

	const visible = ref(false);

	let previousActiveEl = null;
	let resolved = false;

	const MessageBoxBody = defineComponent({
		name: 'MessageBoxBody',
		props: {
			message: { type: [String, Array, Object], default: '' },
		},
		setup(bodyProps) {
			const renderTextLine = (line, index, lines) =>
				h('p', { class: { 'mb-0': index === lines.length - 1 } }, String(line ?? ''));

			return () => {
				const content = Array.isArray(bodyProps.message) ? bodyProps.message : [bodyProps.message];

				if (content.some((item) => isVNode(item))) {
					return content.map((item, index) =>
						isVNode(item) ? cloneVNode(item, { key: item.key ?? index }) : renderTextLine(item, index, content)
					);
				}

				const lines = String(bodyProps.message ?? '').split('\n');

				return lines.map(renderTextLine);
			};
		},
	});

	const settle = (value) => {
		if (resolved) return;
		resolved = true;
		emit('resolve', value);
	};

	const onHide = (event) => {
		if (event?.trigger === 'ok') {
			settle(true);
			return;
		}

		if (event?.trigger === 'cancel') {
			settle(false);
			return;
		}

		settle(null);
	};

	const onHidden = () => {
		settle(null);

		if (previousActiveEl && typeof previousActiveEl.focus === 'function') {
			try {
				previousActiveEl.focus();
			} catch (_) {
			/* noop */
			}
		}
	};

	onMounted(() => {
		previousActiveEl = document.activeElement;

		visible.value = true;
	});
</script>
